import React, { useState, useEffect } from 'react';
import { Modal, Skeleton } from 'antd';
import { EyeOutlined } from '@ant-design/icons';

/**
 * View Category Modal Component with Image Loading States
 * Displays category details in a read-only format
 */
const ViewCategoryModal = ({ 
  visible, 
  onCancel, 
  categoryData 
}) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  // Reset loading state when modal opens
  useEffect(() => {
    if (visible && categoryData?.image) {
      setImageLoading(true);
      setImageError(false);
    }
  }, [visible, categoryData]);

  if (!categoryData) return null;

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <EyeOutlined className="text-blue-500" />
          <span>Category Details</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <div className="mt-4">
        {/* Category Name */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category Name
          </label>
          <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900">
            {categoryData.name}
          </div>
        </div>

        {/* Description */}
        {categoryData.description && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 min-h-[60px]">
              {categoryData.description}
            </div>
          </div>
        )}

        {/* Category Icon Preview */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category Icon
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50">
            <div className="flex justify-center">
              <div className="relative">
                {imageLoading && categoryData.image && !imageError && (
                  <Skeleton.Image
                    active
                    className="w-20 h-20 rounded-lg"
                    style={{ width: 80, height: 80 }}
                  />
                )}
                {categoryData.image && !imageError ? (
                  <img
                    src={categoryData.image}
                    alt={categoryData.name}
                    className={`w-20 h-20 rounded-lg object-cover border border-gray-200 ${
                      imageLoading ? 'hidden' : 'block'
                    }`}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                  />
                ) : null}
                {(imageError || !categoryData.image) && !imageLoading && (
                  <div className="w-20 h-20 rounded-lg bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-400 text-2xl">📦</span>
                  </div>
                )}
              </div>
            </div>
            {imageError && categoryData.image && (
              <p className="text-xs text-red-500 mt-2">Failed to load image</p>
            )}
            {!categoryData.image && (
              <p className="text-xs text-gray-500 mt-2">No image uploaded</p>
            )}
          </div>
        </div>

        {/* Additional Details */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category ID
            </label>
            <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900">
              SFS{categoryData.id}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Products
            </label>
            <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900">
              {categoryData.products || 0}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mt-8">
          <button
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ViewCategoryModal;